// Reduces a tool's code-defined response JSON Schema to a minimal "properties → types" JSON view for
// the read-only preview dialog. The raw schema (pydantic's `model_json_schema`) wraps the fields in
// `$defs`/`$ref`, an internal type name (`ClaimSearchResponse`), `anyOf` and nested `items` that mean
// nothing to an operator. This resolves all of that away and returns a plain value that shows only
// each property and its type — which the dialog renders back as JSON. The full machine-readable
// schema is still offered via the copy button for whoever actually builds the endpoint.

// We only read the handful of JSON Schema keywords we display, so keep values `unknown` and narrow
// defensively — the schema arrives from the API as an opaque `Record<string, unknown>`.
type JsonSchema = Record<string, unknown>;

// One node of the simplified view: a bare type name (`"string"`), a nested object mapping each
// property to its type, or a single-element array wrapping the item type of a list.
export type SimplifiedSchema = string | { [key: string]: SimplifiedSchema } | [SimplifiedSchema];

const asSchema = (value: unknown): JsonSchema | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as JsonSchema) : null;

// Resolve a local `#/$defs/Name` reference against the root schema. pydantic only emits local
// $defs refs; anything else falls through to null and is treated as an opaque object.
const resolveRef = (ref: string, root: JsonSchema): JsonSchema | null => {
  const match = /^#\/\$defs\/(.+)$/.exec(ref);
  if (!match) return null;
  const defs = asSchema(root.$defs);
  return defs ? asSchema(defs[match[1]]) : null;
};

const simplifyProperties = (
  properties: JsonSchema,
  root: JsonSchema,
  seenRefs: Set<string>
): { [key: string]: SimplifiedSchema } =>
  Object.entries(properties).reduce<{ [key: string]: SimplifiedSchema }>((acc, [key, value]) => {
    acc[key] = simplifyNode(asSchema(value) ?? {}, root, seenRefs);
    return acc;
  }, {});

// Simplify one schema node. `seenRefs` tracks the `$ref`s already followed on this branch so a
// self-referential model (pydantic allows recursive schemas) collapses to `"object"` instead of
// recursing forever.
const simplifyNode = (schema: JsonSchema, root: JsonSchema, seenRefs: Set<string>): SimplifiedSchema => {
  let resolved = schema;
  if (typeof schema.$ref === 'string') {
    if (seenRefs.has(schema.$ref)) return 'object';
    const target = resolveRef(schema.$ref, root);
    if (!target) return 'object';
    seenRefs = new Set(seenRefs).add(schema.$ref);
    resolved = target;
  }

  // Collapse pydantic's `Optional[T]` (`anyOf: [T, null]`) down to T.
  if (Array.isArray(resolved.anyOf)) {
    const branch = (resolved.anyOf as unknown[])
      .map(asSchema)
      .find((option): option is JsonSchema => option !== null && option.type !== 'null');
    return branch ? simplifyNode(branch, root, seenRefs) : 'object';
  }

  // An object with declared properties expands into a nested { property: type } map.
  const properties = asSchema(resolved.properties);
  if (properties) return simplifyProperties(properties, root, seenRefs);

  const type = typeof resolved.type === 'string' ? resolved.type : null;
  if (type === 'array') {
    const items = asSchema(resolved.items);
    return [items ? simplifyNode(items, root, seenRefs) : 'object'];
  }
  return type ?? 'object';
};

// Resolve a tool's raw response schema to the minimal properties→types shape. Returns null for a
// non-object schema value (nothing to show).
export const simplifyResponseSchema = (raw: unknown): SimplifiedSchema | null => {
  const root = asSchema(raw);
  if (!root) return null;
  return simplifyNode(root, root, new Set());
};

// A representative sample value for the non-string JSON types. Strings are handled separately by
// `exampleStringForField`, so the value can reflect the field's role rather than a blanket constant.
const EXAMPLE_VALUE_BY_TYPE: Record<string, unknown> = {
  integer: 0,
  number: 0,
  boolean: true,
  null: null,
  object: {},
};

// Shape-appropriate string samples for well-known field roles, matched on the field name. The
// contract carries no sample data, so we infer a value from the field's role (an `email` field gets
// an email, a `*_number`/`*_id` field gets an identifier) — this is what makes the example read like
// a real payload rather than every string being `"example"`. Patterns anchor on word boundaries
// (`_` or string ends) so e.g. `update` is not mistaken for a `date` field.
const STRING_EXAMPLES: { pattern: RegExp; value: string }[] = [
  { pattern: /email/, value: 'name@example.com' },
  { pattern: /(phone|mobile|fax|(^|_)tel(_|$))/, value: '+15551234567' },
  { pattern: /(url|uri|website|href|(^|_)link(_|$))/, value: 'https://example.com' },
  { pattern: /((^|_)(date|datetime|time|timestamp)(_|$)|_at$)/, value: '2024-01-15T09:30:00Z' },
  { pattern: /(number|uuid|guid|(^|_)id$|_id$|(^|_)ref(erence)?(_|$)|(^|_)code(_|$))/, value: '12345' },
];

// Turn a field name into a readable value (`claim_status` -> `"Claim status"`), used as the sample
// for any string field whose role isn't one of the well-known formats above. Still reflects the
// field's role, just without a format-specific shape.
const humanizeFieldName = (fieldName: string): string => {
  const words = fieldName.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'example';
};

// A sample string value derived from the field's role (its name), so the example reads like a real
// payload. Falls back to `"example"` when there is no field name (e.g. a top-level / list-item scalar).
const exampleStringForField = (fieldName?: string): string => {
  if (!fieldName) return 'example';
  const match = STRING_EXAMPLES.find(({ pattern }) => pattern.test(fieldName.toLowerCase()));
  return match ? match.value : humanizeFieldName(fieldName);
};

// Turn the resolved properties→types shape into an example response payload: each leaf type is
// replaced with a sample value, so the operator sees a real example (`"claim_number": "12345"`)
// instead of the field's type (`"claim_number": "string"`). String samples are derived from the
// field name (`fieldName`) so they relate to the field's role rather than all being `"example"`.
export const buildResponseExample = (simplified: SimplifiedSchema, fieldName?: string): unknown => {
  if (typeof simplified === 'string') {
    if (simplified === 'string') return exampleStringForField(fieldName);
    return simplified in EXAMPLE_VALUE_BY_TYPE ? EXAMPLE_VALUE_BY_TYPE[simplified] : exampleStringForField(fieldName);
  }
  if (Array.isArray(simplified)) return [buildResponseExample(simplified[0], fieldName)];
  return Object.entries(simplified).reduce<Record<string, unknown>>((acc, [key, value]) => {
    acc[key] = buildResponseExample(value, key);
    return acc;
  }, {});
};
